import type { ReactElement } from "react";
import { useState } from "react";
import { Flex, SegmentedControl } from "@jsq/ds/core";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { withClientContext } from "~/apollo/client";
import { useGetEntityTransferSettingsQuery } from "~/graphql/mainHooks";
import {
  type GetEntityPreviewGlSyncQueryResult,
  useGetEntityGlSyncSettingsQuery,
} from "~/graphql/supergraphHooks";
import { useDecoupleCanopyTransferSyncFlag } from "~/pages/FundAccounting/flags";
import { toFederationGlobalId, toGlobalId } from "~/utils/graphql-helpers";
import { TransactionCategory } from "../../../../types";
import TransferReviewAndSync from "../../TransferOnlySyncTakeover/ReviewAndSync";
import LoadingState from "../States/LoadingState";
import RegularSyncSummary from "./SyncSummary";
import TransferSummaryBanner from "./TransferSummaryBanner";

type Props = {
  arenaName: string;
  entityName: string | undefined;
  entityId: number;
  arenaId: number;
  data: GetEntityPreviewGlSyncQueryResult["data"] | null | undefined;
  arenaSubdomain: string;
  existingTransactionCategories: TransactionCategory[];
  onBack: () => void;
  onStartSync: (invalidBatchIds: number[]) => Promise<void> | void;
  requestedTransactionCategories: Set<string>;
  endDate: string;
  syncFromTransferHistory: boolean;
  nullJsqPositionIds: string[];
  invalidJsqPositionIds: string[];
};

type TransferSummaryData = {
  batchDate: string;
  positionIds: number[];
};

type TransferGlBatch = {
  glBatchDate?: string;
  positions?: Array<{ positionId: number }>;
};

enum SEGMENT_TYPES {
  transactions = "transactions",
  transfers = "transfers",
}

const SyncSummary = ({
  arenaName,
  entityName,
  entityId,
  arenaId,
  data,
  existingTransactionCategories,
  arenaSubdomain,
  onBack,
  onStartSync,
  requestedTransactionCategories,
  endDate,
  syncFromTransferHistory,
  nullJsqPositionIds,
  invalidJsqPositionIds,
}: Props): ReactElement => {
  const [transferBatchData, setTransferBatchData] = useState<TransferSummaryData[] | null>(
    null,
  );
  const [segment, setSegment] = useState(SEGMENT_TYPES.transactions);
  const decoupleCanopyTransferSync = useDecoupleCanopyTransferSyncFlag();

  const { data: transferSettingsData, loading: transferSettingsLoading } =
    useGetEntityTransferSettingsQuery({
      variables: {
        entityId: toGlobalId("AccountObjectType", entityId || -1),
      },
      context: withClientContext("staff", {
        arenaId: arenaId,
      }),
      fetchPolicy: "no-cache",
      skip: decoupleCanopyTransferSync,
    });
  const entityTransferEnabled =
    transferSettingsData?.investmentEntity?.transferAccountingEnabled || false;
  const globalArenaId = toFederationGlobalId("ArenaObjectType", arenaId ? arenaId : "-1");
  const { data: entityGlSyncSettingsData, loading: entityGlSyncSettingsLoading } =
    useGetEntityGlSyncSettingsQuery({
      variables: {
        arenaId: globalArenaId,
        entityId,
      },

      fetchPolicy: "network-only",
      skip: decoupleCanopyTransferSync,
    });

  const handleOnChange = (value: string) => {
    setSegment(SEGMENT_TYPES[value as keyof typeof SEGMENT_TYPES]);
  };

  const handleTransferLoaded = (transferGlBatchData: TransferGlBatch[]) => {
    let updatedData: TransferSummaryData[] = [];
    if (transferGlBatchData) {
      updatedData = transferGlBatchData.map((batch) => {
        const batchDate = batch.glBatchDate || "";
        const positionIds: number[] = batch.positions?.map((p) => p.positionId) || [];
        return {
          batchDate,
          positionIds,
        };
      });
    }
    setTransferBatchData(updatedData);
  };

  const handleStartSync = async () => {
    await onStartSync([]);
  };

  if (!decoupleCanopyTransferSync && transferSettingsLoading) {
    return <LoadingState />;
  }

  const transferSyncSettingsEnabled =
    !!entityGlSyncSettingsData?.entityGLSyncSettings.transferSettings?.active;

  const couldSyncTransfers =
    !decoupleCanopyTransferSync &&
    transferBatchData &&
    transferBatchData.length > 0 &&
    !entityTransferEnabled &&
    transferSyncSettingsEnabled;

  const transferDataIsLoading =
    !decoupleCanopyTransferSync &&
    (transferSyncSettingsEnabled || entityGlSyncSettingsLoading) &&
    !entityTransferEnabled &&
    transferBatchData == null;

  return (
    <Flex flexDirection="column" alignItems="center" gap={4}>
      {couldSyncTransfers && (
        <ColumnContainer>
          <Column size="xl">
            <TransferSummaryBanner transferCount={transferBatchData.length} />
          </Column>
        </ColumnContainer>
      )}

      {couldSyncTransfers && (
        <SegmentedControl
          onChange={handleOnChange}
          value={segment}
          options={[
            {
              value: "transactions",
              label: "Transactions",
            },
            {
              value: "transfers",
              label: "Transfers",
              disabled: !couldSyncTransfers,
            },
          ]}
        />
      )}

      <div style={{ display: segment === SEGMENT_TYPES.transactions ? "block" : "none" }}>
        <RegularSyncSummary
          arenaName={arenaName}
          entityName={entityName}
          data={data}
          arenaDomain={arenaSubdomain}
          existingTransactionCategories={existingTransactionCategories}
          onStartSync={handleStartSync}
          onBack={onBack}
          requestedTransactionCategories={requestedTransactionCategories}
          isLoading={transferDataIsLoading}
          nullJsqPositionIds={nullJsqPositionIds}
          invalidJsqPositionIds={invalidJsqPositionIds}
        />
      </div>
      {!decoupleCanopyTransferSync && !entityTransferEnabled && transferSyncSettingsEnabled && (
        <div style={{ display: segment === SEGMENT_TYPES.transfers ? "block" : "none" }}>
          <TransferReviewAndSync
            entityId={entityId}
            arenaId={arenaId}
            endDate={endDate}
            onSync={handleStartSync}
            onBack={onBack}
            onLoaded={handleTransferLoaded}
            includeAllTransactions={syncFromTransferHistory}
            startDate={null}
          />
        </div>
      )}
    </Flex>
  );
};
export default SyncSummary;
